import React, { forwardRef, memo } from "react";
import PropTypes from "prop-types";

import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import TablePagination from "@mui/material/TablePagination";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";

import OverflowTip from "../OverflowTip";

const unfiedViewStyles = {
  whiteSpace: "nowrap",
  width: "auto",
  maxWidth: "1000px",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const OverflowTableCell = forwardRef(({ children, width, color, cellStyle, ...props }, ref) => (
  <TableCell
    ref={ref}
    sx={{
      maxWidth: width,
      minWidth: width,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      color: color || "#777",
      ...cellStyle,
    }}
    {...props}
    component="th"
  >
    {children}
  </TableCell>
));

const renderHeaderCell = ({
  id,
  name,
  style,
  width,
  sortable,
  align,
  order,
  orderBy,
  handleSorting,
  boldHeader,
  stickyColCount,
  colIndex,
  showColumnBorder,
  unifiedView,
  renderHeadCell,
}) => (
  <TableCell
    key={id}
    align={align}
    sx={{
      ...style,
      ...(width ? { minWidth: width } : {}),
      ...(boldHeader ? { fontWeight: 600 } : {}),
      ...(stickyColCount >= colIndex + 1 ? { position: "sticky", left: 0, zIndex: 900, background: "white" } : {}),
      ...(unifiedView ? { ...unfiedViewStyles, fontWeight: "600 !important", backgroundColor: "#e8e8e8" } : {}),
    }}
    sortDirection={orderBy === id ? order : false}
    style={{
      borderBottom: "1px solid #D1D5DB",
      ...(showColumnBorder ? { borderRight: "1px solid #D1D5DB", borderBottom: "1px solid #D1D5DB" } : {}),
      ...(showColumnBorder && colIndex === 0 ? { borderLeft: "1px solid #D1D5DB" } : {}),
    }}
  >
    {sortable ? (
      <TableSortLabel
        active={orderBy === id}
        direction={orderBy === id ? order : "asc"}
        onClick={() => handleSorting(id)}
      >
        {renderHeadCell(name)}
      </TableSortLabel>
    ) : (
      renderHeadCell(name)
    )}
  </TableCell>
);

const renderRowCell = ({ row, col, colIndex, index, stickyColCount, onCellClick, unifiedView }) => {
  const { id, renderValueCell, width, style, overflowTooltip, align, showColumnBorder } = col;
  const cellContent = renderValueCell(row[id], row, index);

  return overflowTooltip ? (
    <OverflowTip key={id} title={<Typography variant="body2">{row[id]}</Typography>} arrow placement="right">
      <OverflowTableCell
        width={width}
        {...(onCellClick && {
          onClick: () => onCellClick(row, id),
          sx: { cursor: "pointer" },
        })}
      >
        {cellContent}
      </OverflowTableCell>
    </OverflowTip>
  ) : (
    <TableCell
      key={id}
      align={align}
      sx={{
        ...style,
        ...(width ? { minWidth: width } : {}),
        ...(stickyColCount >= colIndex + 1 ? { position: "sticky", left: 0, zIndex: 800, background: "white" } : {}),
        ...(unifiedView ? unfiedViewStyles : {}),
      }}
      {...(onCellClick && {
        onClick: () => onCellClick(row, id),
        sx: { cursor: "pointer" },
      })}
      style={{
        ...(showColumnBorder ? { borderRight: "1px solid #D1D5DB", borderBottom: "1px solid #D1D5DB" } : {}),
        ...(showColumnBorder && colIndex === 0 ? { borderLeft: "1px solid #D1D5DB" } : {}),
      }}
    >
      {cellContent}
    </TableCell>
  );
};

function KMKTable({
  loading = false,
  columns = [],
  data = [],
  noDataText = "No data available",
  boldHeader = false,
  sortingProperties: { order = "asc", orderBy = "", handleSorting = () => {} } = {},
  tableContainerStyle = {},
  tableProps = {},
  containerHeight = 500,
  pagination = false,
  stickyColCount = 0,
  paginationProperties: {
    rowsPerPageOptions = [],
    count = -1,
    rowsPerPage = -1,
    page = -1,
    onPageChange = () => {},
    handleChangeRowsPerPage = () => {},
    ...OtherPaginationProps
  } = {},
  dataIdKey = "id",
  onRowClick,
  onCellClick,
  unifiedView,
}) {
  return (
    <>
      <TableContainer
        sx={{
          maxHeight: containerHeight,
          marginBottom: "8px",
          "&::-webkit-scrollbar": {
            width: "6px",
            height: "6px",
          },
          "&::-webkit-scrollbar-track": {
            borderRadius: "10px",
          },
          ...tableContainerStyle,
        }}
      >
        <Table {...tableProps} style={{ boxSizing: "border-box" }}>
          {/* border was creating issue so for now instead of tablehead adding tablebody */}
          <TableBody>
            <TableRow sx={{ position: "sticky", top: 0, zIndex: 10, backgroundColor: "#ffffff" }}>
              {columns.map((col, colIndex) =>
                renderHeaderCell({
                  ...col,
                  order,
                  orderBy,
                  handleSorting,
                  boldHeader,
                  stickyColCount,
                  colIndex,
                  unifiedView,
                })
              )}
            </TableRow>
          </TableBody>

          <TableBody>
            {loading || !data.length ? (
              <TableRow>
                <TableCell align="center" colSpan={columns.length}>
                  {loading ? <CircularProgress /> : noDataText}
                </TableCell>
              </TableRow>
            ) : (
              data.map((row, index) => (
                <TableRow
                  key={row[dataIdKey] || index}
                  hover={!!onRowClick}
                  onClick={onRowClick ? () => onRowClick(row, index) : undefined}
                  sx={onRowClick ? { cursor: "pointer" } : {}}
                >
                  {columns.map((col, colIndex) =>
                    renderRowCell({
                      row,
                      col,
                      colIndex,
                      index,
                      stickyColCount,
                      onCellClick,
                      unifiedView,
                    })
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {pagination && (
        <TablePagination
          size="small"
          rowsPerPageOptions={rowsPerPageOptions}
          count={count}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={onPageChange}
          onRowsPerPageChange={handleChangeRowsPerPage}
          {...OtherPaginationProps}
        />
      )}
    </>
  );
}

OverflowTableCell.propTypes = {
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  color: PropTypes.string,
  cellStyle: PropTypes.object,
};

KMKTable.propTypes = {
  loading: PropTypes.bool,
  columns: PropTypes.array.isRequired,
  data: PropTypes.array.isRequired,
  containerHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  dataIdKey: PropTypes.string,
  pagination: PropTypes.bool,
  noDataText: PropTypes.string,
  boldHeader: PropTypes.bool,
  stickyColCount: PropTypes.number,
  paginationProperties: PropTypes.shape({
    rowsPerPageOptions: PropTypes.array,
    count: PropTypes.number,
    rowsPerPage: PropTypes.number,
    page: PropTypes.number,
    onPageChange: PropTypes.func,
    handleChangeRowsPerPage: PropTypes.func,
  }),
  sortingProperties: PropTypes.shape({
    order: PropTypes.oneOf(["asc", "desc"]),
    orderBy: PropTypes.string,
    handleSorting: PropTypes.func,
  }),
  tableContainerStyle: PropTypes.object,
  tableProps: PropTypes.object,
  onRowClick: PropTypes.func,
  onCellClick: PropTypes.func,
  unifiedView: PropTypes.bool,
};

export default memo(KMKTable);
